import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import ClientCard from '../cards/client-card';
import {
	$addClients,
	$clients,
	$cursorClients,
	$hasMoreClients,
	$isFetchingClients,
	$setClients,
	$setCursorClients,
	$setHasMoreClients,
	$setIsFetchingClients,
	type IClient,
} from '@/entities/client.entity';
import {
	$clientsFetchKey,
	$clientsFilter,
	$clientsListScrollTop,
	$clientsSearchField,
	$clientsSearchText,
	$clientsSortBy,
	$clientsSortDir,
	$setClientsFetchKey,
	$setClientsListScrollTop,
	onlineStatusToIsOnline,
	toServerClientsFilters,
} from '@/entities/clients-filter.entity';
import { useVirtualListScrollRestore } from '@/hooks/use-virtual-list-scroll-restore';
import { useUnit } from 'effector-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { isDevMode } from '@/lib/is-dev-mode';
import { LogoLoader } from '@/components/logo-loader';

const LIMIT = 35;

export default function ClientsList() {
	const listRef = useRef<HTMLDivElement | null>(null);
	const [clients, addClients, setClients] = useUnit([$clients, $addClients, $setClients]);
	const [hasMore, setHasMore] = useUnit([$hasMoreClients, $setHasMoreClients]);
	const [cursor, setCursor] = useUnit([$cursorClients, $setCursorClients]);
	const [isFetching, setIsFetching] = useUnit([$isFetchingClients, $setIsFetchingClients]);
	const [filter, searchText, searchField, sortBy, sortDir, savedScrollTop, setScrollTop, fetchKey, setFetchKey] = useUnit([
		$clientsFilter,
		$clientsSearchText,
		$clientsSearchField,
		$clientsSortBy,
		$clientsSortDir,
		$clientsListScrollTop,
		$setClientsListScrollTop,
		$clientsFetchKey,
		$setClientsFetchKey,
	]);
	const [firstFetched, setFirstFetched] = useState(isDevMode);

	const isOnline = onlineStatusToIsOnline(filter.onlineStatus);
	const serverFilters = useMemo(() => toServerClientsFilters(filter), [filter]);
	const currentFetchKey = `${searchField}:${searchText}:${filter.onlineStatus}:${JSON.stringify(serverFilters)}:${sortBy}:${sortDir}`;

	const queryPayload = useMemo(
		() => ({
			limit: LIMIT,
			searchText: searchText || undefined,
			searchField,
			isOnline,
			filters: serverFilters,
			sortBy,
			sortDir,
		}),
		[searchText, searchField, isOnline, serverFilters, sortBy, sortDir],
	);

	const virtualizer = useVirtualizer({
		count: clients.length,
		getScrollElement: () => listRef.current,
		estimateSize: () => 180,
		overscan: 5,
		measureElement: el => el.getBoundingClientRect().height,
		gap: 8,
	});
	const virtualItems = virtualizer.getVirtualItems();

	useVirtualListScrollRestore({
		listType: 'clients',
		listRef,
		virtualizer,
		items: clients,
		savedScrollTop,
		setScrollTop,
		ready: firstFetched && clients.length > 0,
	});

	useEffect(() => {
		if (isDevMode) return;

		function addClientsHandler(data: { clients: IClient[]; cursor: string; hasMore: boolean }) {
			setIsFetching(false);
			if (!firstFetched) setFirstFetched(true);

			if (data?.clients?.length) {
				addClients(data.clients);
				setCursor(data.cursor);
			}
			setHasMore(data.hasMore);
		}

		socket.on(SOCKET_EVENTS.addClients, addClientsHandler);

		return () => {
			socket.off(SOCKET_EVENTS.addClients, addClientsHandler);
		};
	}, []);

	useEffect(() => {
		if (isDevMode) {
			setFirstFetched(true);
			return;
		}

		function fetch() {
			setIsFetching(true);
			setClients([]);
			setCursor(null);
			setScrollTop(0);
			setFetchKey(currentFetchKey);
			socket.emit(SOCKET_EVENTS.getClients, { ...queryPayload, cursor: null });
		}

		if (clients.length > 0 && fetchKey === currentFetchKey) {
			setFirstFetched(true);
			return;
		}

		if (!firstFetched) {
			fetch();
		} else {
			const timeout = setTimeout(fetch, 250);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [currentFetchKey]);

	useEffect(() => {
		if (!hasMore || isFetching) return;

		if (virtualItems.length > 0) {
			const lastItem = virtualItems[virtualItems.length - 1];
			if (lastItem.index >= clients.length - 10) {
				if (socket.connected) {
					setIsFetching(true);
					socket.emit(SOCKET_EVENTS.getClients, { ...queryPayload, cursor });
				}
			}
		}
	}, [virtualItems, clients.length, isFetching, hasMore, cursor, queryPayload]);

	return (
		<div ref={listRef} data-scroll-container className='h-full overflow-auto pr-1 px-1'>
			{!firstFetched || (isFetching && !clients.length) ? (
				<div className='flex items-center justify-center mt-30'>
					<LogoLoader />
				</div>
			) : clients.length === 0 ? (
				<div className='text-center mt-30 text-md'>Клиенты не найдены</div>
			) : (
				<div
					style={{
						height: `${virtualizer.getTotalSize()}px`,
						width: '100%',
						position: 'relative',
					}}
				>
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
						}}
					>
						{virtualItems.map(({ index, key }) => {
							const card = clients[index];
							return (
								<div key={key} data-index={index} ref={virtualizer.measureElement} className='pb-2'>
									<ClientCard client={card} />
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
